import cleanse from '../../config/util/cleanse';
import { DetectionReason, DetectionType } from '../../reporter/constants';
import { Report } from '../../reporter/report/Report';
import { SourceType } from '../../webrtc/agent/interfaces';

export type ReportRecord = Record<string, unknown> | Report;
export interface ReportBody {
  contentId: string;
  tenantId: string;
  record: ReportRecord;
  token: string;
}
export type Body = ReportBody | Record<string, unknown>;
export interface RequestReport {
  url: string;
  body: Body;
}

export const MAX_SIZE = 64 * 1024;

export function isValidSize(json: string, maxSize: number): boolean {
  // assuming a char == 8 bits (Utf-8)
  return json.length <= maxSize;
}

export function minimizeReport(report: Report): Report {
  const reason = DetectionReason.TRIMMED;
  const type = DetectionType.REPORTER;
  return {
    detection: { data: [{ type, reason }] },
    ecdn11: cleanse(report.ecdn11),
    general: cleanse(report.general),
    events: report.events,
    transfers: report.transfers
  } as Report;
}

export function minimizeTransfers(report: Report): Report {
  report.transfers.data.forEach(t => {
    delete t.tenantId;
    delete t.contentId;
    delete t.objectId;
    if (t.type === SourceType.INGEST) {
      t.sourceId = 'origin';
    }
  });
  return report;
}

export function trimTransfers(report: Report, bytesToTrim: number): Report {
  let trimmed = 0;

  const trs = report.transfers;
  while (trimmed < bytesToTrim && trs.data.length > 0) {
    const t = trs.data.pop();
    if (t) {
      const ts = JSON.stringify(t);
      trimmed = trimmed + ts.length;
    }
  }
  return report;
}

export function maybeStripPayload(body: Body, maxSize: number): Body {
  // if the request contains a record field it is an ECDN11 report.
  if (body.record) {
    let asJson = JSON.stringify(body);
    if (isValidSize(asJson, maxSize)) {
      return body;
    }
    /// FIRST STEP ///
    // modify body
    const report = body.record as Report;
    // trim  to essentials
    body.record = minimizeReport(report);

    asJson = JSON.stringify(body);
    if (isValidSize(asJson, maxSize)) {
      return body;
    }

    /// SECOND STEP ///
    // in this case we remove tid, cid, and oid to reduce total overhead.
    body.record = minimizeTransfers(body.record as Report);

    asJson = JSON.stringify(body);
    if (
      isValidSize(asJson, maxSize) ||
      // if there arent any transfer there's nothing to do
      (body.record as Report).transfers.data.length < 1
    ) {
      return body;
    }

    /// THIRD STEP ///
    // calculate what's left to remove and pop off transfers
    const toTrim = Math.abs(asJson.length - MAX_SIZE);
    if (toTrim > 0) {
      body.record = trimTransfers(body.record as Report, toTrim);
    }
  }
  return body;
}

export default function sendBeaconReport(request: RequestReport): boolean {
  const { url, body } = request;
  const payload = maybeStripPayload(body, MAX_SIZE);
  return window.navigator.sendBeacon(url, JSON.stringify(payload));
}
